import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { AppLayout } from '../components/layout/AppLayout';
import { Loader } from '../components/common/Loader';
import { useApi } from '../hooks/useApi';
import { getClinicInfo } from '../services/clinicService';
import { apiClient } from '../services/apiClient';

export const BookingPage: React.FC = () => {
  const [description, setDescription] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { isLoading, data: clinicData } = useApi(getClinicInfo, { autoExecute: true });

  const appointmentLink = clinicData?.clinic?.appointmentLink || null;

  const handleBookOnline = () => {
    if (appointmentLink) {
      window.open(appointmentLink, '_blank');
    }
  };

  const handleSubmitRequest = async () => {
    if (!description.trim()) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiClient.post('/webapp/appointment-requests', {
        description: description.trim(),
      });
      setShowDialog(false);
      setDescription('');
      setSuccess(true);
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Ошибка при отправке заявки');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout title="Запись">
        <Loader message="Загрузка..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Запись на приём">
      <Box>
        <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 3 }}>
          Выберите удобный способ записи
        </Typography>

        <Grid container spacing={2}>
          {/* Онлайн запись */}
          {appointmentLink && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 1 }}>
                    Онлайн запись
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
                    Выберите удобное время и запишитесь самостоятельно через сайт
                  </Typography>
                  <Button variant="contained" fullWidth onClick={handleBookOnline}>
                    Записаться онлайн
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Запись через менеджера */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 1 }}>
                  Через менеджера
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
                  Опишите ваши пожелания — мы свяжемся с вами и подберём удобное время
                </Typography>
                <Button variant="outlined" fullWidth onClick={() => setShowDialog(true)}>
                  Оставить заявку
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Диалог заявки */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Заявка на запись</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            Опишите ваши пожелания: желаемое время, врача, услугу и т.д.
          </Typography>
          {submitError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {submitError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Например: хочу записаться к стоматологу в ближайшую субботу после 14:00"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} disabled={submitting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={!description.trim() || submitting}
          >
            {submitting ? 'Отправка...' : 'Отправить заявку'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        message="Заявка отправлена! Мы свяжемся с вами в ближайшее время."
      />
    </AppLayout>
  );
};

